import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { pageView, PAGES } from '../../utils/analytics-tracker'

const FAQContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  color: ${({ theme }) => theme.bodyText};
`

const FAQHeader = styled.div`
  margin-bottom: 3rem;
  text-align: center;
  
  h1 {
    color: ${({ theme }) => theme.bodyText};
    font-size: 2.5rem;
    margin-bottom: 1rem;
  }
  
  p {
    color: ${({ theme }) => theme.bodyText};
    opacity: 0.8;
    font-size: 1.1rem;
  }
`

const FAQItem = styled.div`
  margin-bottom: 1.5rem;
  border: 1px solid ${({ theme }) => theme.borderColor};
  border-radius: 8px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.secondaryBackground};
`

const Question = styled.button`
  width: 100%;
  padding: 1.5rem;
  text-align: left;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${({ theme }) => theme.bodyText};
  font-size: 1.1rem;
  font-weight: 600;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: ${({ theme }) => theme.primaryBackground};
  }
  
  &:focus {
    outline: 2px solid ${({ theme }) => theme.primary};
    outline-offset: -2px;
  }
`

const QuestionText = styled.span`
  flex: 1;
  padding-right: 1rem;
`

const ExpandIcon = styled.span`
  font-size: 1.5rem;
  color: ${({ theme }) => theme.primary};
  transition: transform 0.3s;
  transform: ${({ $isOpen }) => $isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`

const Answer = styled.div`
  padding: ${({ $isOpen }) => $isOpen ? '0 1.5rem 1.5rem 1.5rem' : '0 1.5rem'};
  max-height: ${({ $isOpen }) => $isOpen ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;
  color: ${({ theme }) => theme.bodyText};
  opacity: 0.9;
  line-height: 1.6;
  
  p {
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  strong {
    color: ${({ theme }) => theme.primary};
    font-weight: 600;
  }
  
  ul {
    margin: 1rem 0;
    padding-left: 2rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`

const faqData = [
  {
    question: 'Can other users see my songs?',
    answer: `
      <p><strong>No, your songs are private by default.</strong> Only you can see and edit your songs unless you explicitly share them.</p>
      <p>Your songs remain completely private until you choose to share them with specific users or groups. This gives you full control over who has access to your content.</p>
    `
  },
  {
    question: 'How do I share songs with other people?',
    answer: `
      <p>There are several ways to share your songs with others:</p>
      <ul>
        <li><strong>Individual Sharing:</strong> Open a song, click the "Permissions" or "Share" button, and enter the email address of the person you want to share with. You can set their permission level (view or edit).</li>
        <li><strong>Group Sharing:</strong> Add your song to a group you're a member of. All group members will have access based on the group's permission settings.</li>
        <li><strong>Invitations:</strong> When you share with someone who doesn't have an account yet, they'll receive an email invitation to join Tabify and access your shared content.</li>
      </ul>
      <p>You can manage all your shared content and revoke access at any time through the permissions settings.</p>
    `
  },
  {
    question: 'How should I use groups?',
    answer: `
      <p><strong>Groups are perfect for bands, worship teams, or any collaborative music projects.</strong></p>
      <p>Here's how to make the most of groups:</p>
      <ul>
        <li><strong>Band/Team Collaboration:</strong> Create a group for your band or team. All members can access shared songs, setlists, and shows.</li>
        <li><strong>Organize by Project:</strong> Create separate groups for different projects, venues, or types of performances.</li>
        <li><strong>Permission Levels:</strong> Assign roles to group members - admins can manage the group and all content, while regular members can view and contribute based on permissions.</li>
        <li><strong>Centralized Content:</strong> Share songs, setlists, and shows with the entire group at once instead of sharing individually.</li>
        <li><strong>Rehearsal Coordination:</strong> Use groups to track which songs need practice and coordinate rehearsal schedules.</li>
      </ul>
      <p>Groups make it easy to keep everyone on the same page and ensure your whole team has access to the latest versions of your music.</p>
    `
  },
  {
    question: 'What are setlists and how do I use them?',
    answer: `
      <p><strong>Setlists are ordered collections of songs for performances or rehearsals.</strong></p>
      <p>Create a setlist by clicking "New Setlist" and adding songs in the order you'll perform them. You can:</p>
      <ul>
        <li>Drag and drop to reorder songs</li>
        <li>See total duration of your setlist</li>
        <li>Share setlists with band members or groups</li>
        <li>Link setlists to shows for performance tracking</li>
        <li>Print or view setlists during performances</li>
      </ul>
    `
  },
  {
    question: 'How do I transpose a song to a different key?',
    answer: `
      <p>Open any song and use the transpose controls (usually + and - buttons) to shift the key up or down.</p>
      <p>The chords will automatically update to the new key while preserving the song structure. This is perfect for matching a singer's range or playing with different instruments.</p>
    `
  },
  {
    question: 'Can I use Tabify offline?',
    answer: `
      <p><strong>Yes, the mobile app supports offline access.</strong></p>
      <p>Songs you've recently viewed are cached on your device and available offline. You can view songs, transpose them, and use the metronome without an internet connection.</p>
      <p>Changes made offline will sync automatically when you reconnect to the internet.</p>
    `
  },
  {
    question: 'How do I track rehearsals?',
    answer: `
      <p>Use the Rehearsal feature to track which songs you've practiced and when.</p>
      <p>Mark songs as rehearsed to keep track of your progress. This helps you identify which songs need more practice before a performance.</p>
      <p>Rehearsal history is shared with your group members so everyone knows which songs are performance-ready.</p>
    `
  },
  {
    question: 'What permission levels are available?',
    answer: `
      <p>Tabify offers three permission levels for shared content:</p>
      <ul>
        <li><strong>View:</strong> Can see the song/setlist/show but cannot make changes</li>
        <li><strong>Edit:</strong> Can view and modify the content, including chords, lyrics, and settings</li>
        <li><strong>Owner:</strong> Has full control including edit permissions plus the ability to manage who has access to the content and change their permission levels</li>
      </ul>
      <p>As the original creator, you are automatically the owner and always have full control. You can grant ownership to others if needed.</p>
    `
  }
]

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null)

  useEffect(() => {
    pageView(PAGES.FAQ)
  }, [])

  const toggleQuestion = (index) => {
    setOpenIndex(openIndex === index ? null : index)
  }

  return (
    <FAQContainer>
      <FAQHeader>
        <h1>Frequently Asked Questions</h1>
        <p>Find answers to common questions about using Tabify</p>
      </FAQHeader>

      {faqData.map((faq, index) => (
        <FAQItem key={index}>
          <Question onClick={() => toggleQuestion(index)}>
            <QuestionText>{faq.question}</QuestionText>
            <ExpandIcon $isOpen={openIndex === index}>▼</ExpandIcon>
          </Question>
          <Answer
            $isOpen={openIndex === index}
            dangerouslySetInnerHTML={{ __html: faq.answer }}
          />
        </FAQItem>
      ))}
    </FAQContainer>
  )
}

export default FAQ
